/**
 * Silent session restore on the desktop, and the opt-in that enables it.
 *
 * The web build cannot restore a signing session on reload, and that is a property
 * of the browser rather than a gap: the key is only recoverable with a passphrase,
 * and there is nowhere to put a passphrase that a script on the origin cannot also
 * read. `identity/storage.ts` says as much and says it must not be "fixed".
 *
 * The desktop shell has somewhere. So the flow is unchanged — locked identity,
 * `unlock(passphrase)`, session — with the passphrase coming from the OS keychain
 * instead of from a dialog. No new signer, no second storage format, no session
 * kind: `SessionProvider` cannot tell the difference between this and a fast typist.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useSession } from "../identity/SessionProvider";
import {
  dropPassphrase,
  hasStoredPassphrase,
  loadPassphrase,
  reconcileKeychain,
  storePassphrase,
} from "./keychain";
import { isDesktopShell } from "./runtime";

/**
 * Try the keychain whenever an identity is locked, and keep the keychain honest
 * about which accounts still exist.
 *
 * Mounted once, from the desktop bridge.
 */
export function useKeychainRestore(): void {
  const { locked, unlock, accounts } = useSession();

  /*
   * One attempt per pubkey, for the life of the document.
   *
   * The alternative is a loop: a failed unlock leaves the identity locked, the
   * effect re-runs on the same `locked` value, and the keychain is asked again —
   * on macOS with a modal prompt each time. A ref rather than state because
   * recording the attempt must not itself cause a render.
   */
  const attempted = useRef(new Set<string>());

  useEffect(() => {
    if (!isDesktopShell()) return;
    // Nothing to supply for an extension lock: the remedy there is the extension
    // itself, and `unlock` ignores the passphrase for that kind.
    if (!locked?.ncryptsec) return;

    const pubkey = locked.pubkey;
    if (attempted.current.has(pubkey)) return;
    attempted.current.add(pubkey);

    void (async () => {
      const passphrase = await loadPassphrase(pubkey);
      if (passphrase === undefined) return;

      /*
       * `false` means the passphrase is wrong; a throw means something else is.
       *
       * The distinction is the same one `UnlockDialog` makes and it matters more
       * here, because the response to "wrong" is to delete the stored secret. A
       * bunker that is asleep throws, and deleting the passphrase for that would
       * mean the user has to type it again — for a failure that had nothing to do
       * with it.
       */
      let unlocked: boolean;
      try {
        unlocked = await unlock(passphrase);
      } catch {
        return;
      }
      if (!unlocked) await dropPassphrase(pubkey);
    })();
  }, [locked, unlock]);

  /*
   * A stored passphrase must not outlive the credential it unlocks.
   *
   * `SessionProvider` forgets an account on sign-out and on removal, and knows
   * nothing about a keychain — deliberately, since it is shared with the web build.
   * Reconciling against the account list from here is what closes the loop without
   * pushing a platform detail into the session layer: whatever is gone from the
   * list has its secret deleted on the next pass.
   */
  useEffect(() => {
    if (!isDesktopShell()) return;
    void reconcileKeychain(accounts.map((account) => account.pubkey));
  }, [accounts]);
}

export interface KeychainOffer {
  /** False in a browser. Callers render nothing at all in that case. */
  readonly available: boolean;
  /** Whether the user wants this device to remember the passphrase. */
  readonly enabled: boolean;
  setEnabled(next: boolean): void;
  /**
   * Apply the current choice to a passphrase that has just worked.
   *
   * Call this only after a successful unlock. Storing an unverified passphrase
   * would persist a typo and then present the resulting failed restores as a
   * broken keychain.
   */
  apply(passphrase: string): void;
}

const UNAVAILABLE: KeychainOffer = {
  available: false,
  enabled: false,
  setEnabled: () => {},
  apply: () => {},
};

/**
 * The "remember on this device" choice for one account.
 *
 * Defaults to **off**, and stays off unless the account's owner turns it on. A
 * stored passphrase means a script that reaches the page can ask for it and then
 * sign, which a locked identity otherwise makes impossible — so this is a trade
 * the user makes knowingly, per account, rather than one the app makes for them.
 *
 * Defaults to on for an account that already has a stored secret, so unlocking
 * again after a stale-passphrase failure re-stores the new one instead of silently
 * turning the feature off.
 */
export function useKeychainOffer(pubkey: string | undefined): KeychainOffer {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    setEnabled(pubkey !== undefined && hasStoredPassphrase(pubkey));
  }, [pubkey]);

  const apply = useCallback(
    (passphrase: string) => {
      if (pubkey === undefined) return;
      // Unticking is a request to forget, not merely to skip storing: the box
      // shows the desired state of the device, so applying it has to be able to
      // remove a secret stored on an earlier unlock.
      void (enabled
        ? storePassphrase(pubkey, passphrase)
        : dropPassphrase(pubkey));
    },
    [pubkey, enabled],
  );

  if (!isDesktopShell()) return UNAVAILABLE;
  return { available: true, enabled, setEnabled, apply };
}
